import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBookOpen,
  FiClock,
  FiPlay,
  FiCheck,
  FiTarget,
  FiTrendingUp,
  FiActivity,
  FiBarChart,
  FiCircle,
  FiTriangle,
  FiSquare,
  FiBox,
  FiPieChart,
  FiRefreshCw,
  FiAlertCircle,
  FiCalendar,
  FiArrowRight,
  FiBarChart2,
  FiBook,
  FiClipboard,
  FiZap,
  FiGitMerge,
  FiHexagon,
  FiCpu,
} from 'react-icons/fi';
import {
  getComprehensiveQuantProgress,
  clearQuantProgressCache,
} from './api/progressAPI';
import {
  redirectToLoginWithReturn,
  setLocalStorageWithSync,
} from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import {
  getQuantTrack,
  getQuantTrackLabel,
  getRecommendedQuantTrack,
  setQuantTrackChoice,
  QUANT_TRACKS,
} from './quantProfileUtils';
import {
  getEffectiveQuantWeekNumber,
  getQuantWeekendModule,
  QUANT_WEEKEND_MODULES,
} from './quantWeekendPlan';

// CACHING BEHAVIOR:
// - Data is cached in localStorage with user email as key
// - Cache expires after 1 hour (3600000ms)
// - Fresh data is fetched only when cache is missing or expired
// - Manual refresh (refresh button) clears cache and fetches fresh data
// - Cache key format: 'liwaza_quant_progress_{userEmail}'

// Lesson data structure
const conceptsData = [
  {
    id: 'arithmetic-foundations',
    title: 'Arithmetic Foundations',
    description:
      'Build confidence with operations, negatives, fractions, decimals, and percents',
    icon: <FiBookOpen />,
    difficulty: 'Foundations',
    estimatedTime: '45 minutes',
    topics: [
      'Order of Operations',
      'Negative Numbers',
      'Fractions',
      'Decimals & Percents',
    ],
    completed: false,
  },
  {
    id: 'math-translation',
    title: 'Math Translation',
    description:
      'Turn GRE word problems into variables, expressions, equations, and units',
    icon: <FiClipboard />,
    difficulty: 'Foundations',
    estimatedTime: '45 minutes',
    topics: [
      'Define Variables',
      'Comparison Words',
      'Part-Whole Setup',
      'Unit Setup',
    ],
    completed: false,
  },
  {
    id: 'math-shortcuts',
    title: 'Math Shortcuts & Mental Math',
    description:
      'Master calculation tricks and mental math techniques to solve GRE problems faster',
    icon: <FiZap />,
    difficulty: 'Basic',
    estimatedTime: '45 minutes',
    topics: [
      'Multiplication Tricks',
      'Division Shortcuts',
      'Squaring Numbers',
      'Estimation',
      'Fractions & Percentages',
    ],
    completed: false,
  },
  {
    id: 'integers',
    title: 'Properties and Types of Integers',
    description:
      'Learn about prime numbers, factors, multiples, and integer properties',
    icon: <FiTarget />,
    difficulty: 'Basic',
    estimatedTime: '45 minutes',
    topics: [
      'Prime Numbers',
      'Factors & Multiples',
      'Even & Odd',
      'Divisibility Rules',
    ],
    completed: false,
  },
  {
    id: 'percentage-ratio',
    title: 'Percentage & Ratio',
    description: 'Master percentage calculations, ratios, and proportions',
    icon: <FiPieChart />,
    difficulty: 'Basic',
    estimatedTime: '40 minutes',
    topics: ['Percentage Basics', 'Percentage Change', 'Ratios', 'Proportions'],
    completed: false,
  },
  {
    id: 'exponents',
    title: 'Exponents & Roots',
    description: 'Master exponent rules, square roots, and radical expressions',
    icon: <FiZap />,
    difficulty: 'Basic',
    estimatedTime: '45 minutes',
    topics: ['Exponent Rules', 'Square Roots', 'Radical Expressions', 'Powers'],
    completed: false,
  },
  {
    id: 'algebra',
    title: 'Algebra',
    description:
      'Linear equations, quadratics, inequalities, and algebraic expressions',
    icon: <FiActivity />,
    difficulty: 'Intermediate',
    estimatedTime: '60 minutes',
    topics: [
      'Linear Equations',
      'Quadratic Equations',
      'Inequalities',
      'Systems of Equations',
    ],
    completed: false,
  },
  {
    id: 'sequences',
    title: 'Sequences',
    description:
      'Recognize arithmetic, geometric, recursive, and series patterns',
    icon: <FiTrendingUp />,
    difficulty: 'Intermediate',
    estimatedTime: '45 minutes',
    topics: [
      'Arithmetic Sequences',
      'Geometric Sequences',
      'Recursive Rules',
      'Series Sums',
    ],
    completed: false,
  },
  {
    id: 'absolute-value',
    title: 'Absolute Value',
    description:
      'Solve absolute value equations, inequalities, and distance problems',
    icon: <FiTarget />,
    difficulty: 'Intermediate',
    estimatedTime: '45 minutes',
    topics: [
      'Absolute Value as Distance',
      'Equations',
      'Two Absolute Values',
      'Less-Than Inequalities',
      'Greater-Than Inequalities',
    ],
    completed: false,
  },
  {
    id: 'functions',
    title: 'Functions',
    description:
      'Master function notation, domain & range, composition, inverse functions, and transformations',
    icon: <FiCpu />,
    difficulty: 'Intermediate',
    estimatedTime: '55 minutes',
    topics: [
      'Function Notation',
      'Domain & Range',
      'Composition',
      'Inverse Functions',
      'Transformations',
    ],
    completed: false,
  },
  {
    id: 'sets',
    title: 'Sets & Venn Diagrams',
    description: 'Master set theory, unions, intersections, and Venn diagrams',
    icon: <FiGitMerge />,
    difficulty: 'Intermediate',
    estimatedTime: '45 minutes',
    topics: ['Set Operations', 'Venn Diagrams', 'Intersections', 'Complements'],
    completed: false,
  },
  {
    id: 'probabilities',
    title: 'Probabilities',
    description:
      'Master probability concepts, compound events, conditional probability, and combinatorics',
    icon: <FiPieChart />,
    difficulty: 'Intermediate',
    estimatedTime: '55 minutes',
    topics: [
      'Basic Probability',
      'Compound Probability',
      'Conditional Probability',
      'Combinations & Permutations',
    ],
    completed: false,
  },
  {
    id: 'work-rate',
    title: 'Work & Rate Problems',
    description:
      'Solve work rate problems, combined work scenarios, and efficiency calculations',
    icon: <FiActivity />,
    difficulty: 'Intermediate',
    estimatedTime: '50 minutes',
    topics: [
      'Individual Work Problems',
      'Combined Work',
      'Rate Differences',
      'Complex Work Scenarios',
    ],
    completed: false,
  },
  {
    id: 'distance-rate',
    title: 'Distance, Rate & Time',
    description:
      'Master distance-rate-time problems, relative motion, and multi-leg journeys',
    icon: <FiTrendingUp />,
    difficulty: 'Intermediate',
    estimatedTime: '50 minutes',
    topics: [
      'Basic Distance-Rate-Time',
      'Relative Motion',
      'Multi-Leg Journeys',
      'Advanced Scenarios',
    ],
    completed: false,
  },
  {
    id: 'coordinate-geometry',
    title: 'Coordinate Geometry',
    description: 'Points, lines, slopes, and distance in the coordinate plane',
    icon: <FiBarChart />,
    difficulty: 'Intermediate',
    estimatedTime: '50 minutes',
    topics: ['Points & Lines', 'Slope', 'Distance Formula', 'Midpoint Formula'],
    completed: false,
  },
  {
    id: 'circles',
    title: 'Circles',
    description: 'Circle properties, area, circumference, and arc measures',
    icon: <FiCircle />,
    difficulty: 'Intermediate',
    estimatedTime: '45 minutes',
    topics: [
      'Circle Properties',
      'Area & Circumference',
      'Arcs & Sectors',
      'Tangents',
    ],
    completed: false,
  },
  {
    id: 'triangles',
    title: 'Triangles',
    description: 'Triangle properties, area, perimeter, and special triangles',
    icon: <FiTriangle />,
    difficulty: 'Intermediate',
    estimatedTime: '55 minutes',
    topics: [
      'Triangle Types',
      'Pythagorean Theorem',
      'Special Triangles',
      'Area Calculations',
    ],
    completed: false,
  },
  {
    id: 'quadrilaterals',
    title: 'Quadrilaterals',
    description:
      'Properties of squares, rectangles, parallelograms, and trapezoids',
    icon: <FiSquare />,
    difficulty: 'Intermediate',
    estimatedTime: '40 minutes',
    topics: ['Squares & Rectangles', 'Parallelograms', 'Trapezoids', 'Rhombus'],
    completed: false,
  },
  {
    id: 'polygons',
    title: 'Polygons',
    description:
      'Properties of polygons, interior angles, and regular polygons',
    icon: <FiHexagon />,
    difficulty: 'Intermediate',
    estimatedTime: '35 minutes',
    topics: [
      'Polygon Properties',
      'Interior Angles',
      'Exterior Angles',
      'Regular Polygons',
    ],
    completed: false,
  },
  {
    id: 'three-dimensional-figures',
    title: '3-Dimensional Figures',
    description:
      'Properties of cubes, rectangular prisms, cylinders, and spheres',
    icon: <FiBox />,
    difficulty: 'Advanced',
    estimatedTime: '50 minutes',
    topics: ['Rectangular Prisms', 'Cylinders', 'Spheres', 'Surface Area'],
    completed: false,
  },
  {
    id: 'volumes',
    title: 'Volumes & Surface Area',
    description: 'Calculate volumes and surface areas of various 3D shapes',
    icon: <FiBox />,
    difficulty: 'Advanced',
    estimatedTime: '45 minutes',
    topics: [
      'Volume Formulas',
      'Surface Area',
      'Composite Figures',
      'Applications',
    ],
    completed: false,
  },
  {
    id: 'statistics',
    title: 'Statistics: Mean, Median & Mode',
    description:
      'Master measures of central tendency, quartiles, percentiles, and box plots',
    icon: <FiBarChart />,
    difficulty: 'Basic',
    estimatedTime: '50 minutes',
    topics: [
      'Mean',
      'Median',
      'Mode',
      'Quartiles & Percentiles',
      'Box Plots',
      'IQR & Outliers',
    ],
    completed: false,
  },
  {
    id: 'graphs',
    title: 'Graphs & Data Interpretation',
    description:
      'Master reading and interpreting bar graphs, line graphs, pie charts, and data tables',
    icon: <FiTrendingUp />,
    difficulty: 'Intermediate',
    estimatedTime: '50 minutes',
    topics: ['Bar Graphs', 'Line Graphs', 'Pie Charts', 'Data Interpretation'],
    completed: false,
  },
];

// Helper function to get progress data for other components
export const getConceptsMasteryProgressData = (setToast) => {
  try {
    const completedLessons = JSON.parse(
      localStorage.getItem('concepts_mastery_progress') || '[]'
    );
    const totalLessons = conceptsData.length;
    const completedCount = completedLessons.length;
    const accuracy =
      completedLessons.length > 0
        ? Math.round(
            completedLessons.reduce(
              (sum, lesson) => sum + (lesson.score || 0),
              0
            ) / completedLessons.length
          )
        : 0;

    return {
      totalLessons,
      completedLessons: completedCount,
      accuracy,
      averageScore: accuracy,
      lastUpdated:
        completedLessons.length > 0 ? new Date().toISOString() : null,
    };
  } catch (error) {
    console.error('Error getting concepts mastery progress:', error);
    return {
      totalLessons: 0,
      completedLessons: 0,
      accuracy: 0,
      averageScore: 0,
      lastUpdated: null,
    };
  }
};

// Helper function to save lesson completion
export const saveConceptsLessonCompletion = async (lessonResult, setToast) => {
  try {
    // Import the core API function directly
    const { savePracticeCompletion } = await import('./api/quantPracticeAPI');

    // Calculate metrics and prepare data in one step
    const totalQuestions = lessonResult.totalQuestions || 5;
    const correctAnswers = Math.round(
      (lessonResult.score / 100) * totalQuestions
    );

    const practiceData = {
      lessonId: lessonResult.lessonId,
      lessonName: getLessonNameById(lessonResult.lessonId),
      practiceMode: lessonResult.practiceMode || 'untimed',
      totalQuestions: totalQuestions,
      correctAnswers: correctAnswers,
      totalTimeSeconds: lessonResult.timeSpent
        ? Math.round(lessonResult.timeSpent / 1000)
        : null,
      sessionStartTime:
        lessonResult.sessionStartTime || new Date().toISOString(),
      sessionEndTime: new Date().toISOString(),
      questionsData: lessonResult.questionsData || {},
      deviceInfo: navigator.userAgent,
    };

    // Try backend first, fallback to localStorage
    try {
      const result = await savePracticeCompletion(practiceData, setToast);
      console.log('Practice completion saved to backend:', result);

      // Also save to localStorage as backup
      await saveToLocalStorage(lessonResult);

      // Clear cache to ensure fresh data on next load
      // Get user email from session storage
      try {
        const user = getStoredUser();
        if (user?.email) {
          clearQuantProgressCache(user.email);
        }
        const { clearReadinessCache } = await import('./api/quantPracticeAPI');
        clearReadinessCache();
      } catch (cacheError) {
        console.warn('Failed to clear progress cache:', cacheError);
      }

      return true;
    } catch (backendError) {
      console.warn(
        'Backend save failed, using localStorage fallback:',
        backendError
      );
      return await saveToLocalStorage(lessonResult);
    }
  } catch (error) {
    console.error('Error saving lesson completion:', error);
    return await saveToLocalStorage(lessonResult);
  }
};

// Helper function to save to localStorage (fallback)
const saveToLocalStorage = async (lessonResult) => {
  try {
    const existingProgress = JSON.parse(
      localStorage.getItem('concepts_mastery_progress') || '[]'
    );
    const filteredProgress = existingProgress.filter(
      (lesson) => lesson.lessonId !== lessonResult.lessonId
    );

    filteredProgress.push({
      lessonId: lessonResult.lessonId,
      score: lessonResult.score,
      completedAt: new Date().toISOString(),
      timeSpent: lessonResult.timeSpent || 0,
      practiceMode: lessonResult.practiceMode || 'untimed',
    });

    await setLocalStorageWithSync(
      'concepts_mastery_progress',
      JSON.stringify(filteredProgress)
    );
    return true;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return false;
  }
};

// Helper function to get lesson name by ID
const getLessonNameById = (lessonId) => {
  const conceptData = conceptsData.find((concept) => concept.id === lessonId);
  return conceptData ? conceptData.title : lessonId;
};

export default function ConceptsMastery() {
  const navigate = useNavigate();
  const authRedirectStartedRef = useRef(false);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [selectedDifficulty, setSelectedDifficulty] = useState('ALL');
  // eslint-disable-next-line no-unused-vars
  const [selectedTopic, setSelectedTopic] = useState('ALL');
  const [backendProgress, setBackendProgress] = useState(null);
  const [comprehensiveProgress, setComprehensiveProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantTrack, setQuantTrack] = useState(() => getQuantTrack());

  // Get user email from session storage or URL params
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(() => getStoredUser());

  const [userEmail, setUserEmail] = useState(null);

  const redirectToLogin = (
    message = 'Votre session a expire. Veuillez vous reconnecter.'
  ) => {
    if (authRedirectStartedRef.current) {
      return;
    }

    authRedirectStartedRef.current = true;
    redirectToLoginWithReturn({
      navigate,
      redirectTo: window.location.pathname + window.location.search,
      message,
    });
  };

  // Set userEmail from URL params, user object, or localStorage fallback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlUserEmail = urlParams.get('user_email');

    if (urlUserEmail) {
      setUserEmail(urlUserEmail);
    } else if (user?.email) {
      setUserEmail(user.email);
    } else {
      const storedEmail = localStorage.getItem('userEmail');
      if (storedEmail) {
        setUserEmail(storedEmail);
      }
    }
  }, [user]);

  // Load the persisted Quant track without forcing the Foundations lesson filter.
  // Students should see every lesson by default and can still filter manually.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setQuantTrack(getQuantTrack());
  }, []);

  useEffect(() => {
    if (userEmail) {
      loadProgressData();
    }
  }, [userEmail]);

  const loadProgressData = async () => {
    if (!userEmail) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Single unified backend call with caching
      const comprehensiveData = await getComprehensiveQuantProgress(userEmail);

      if (comprehensiveData) {
        // Set comprehensive progress for weekly progression
        setComprehensiveProgress(comprehensiveData);

        // Process lesson statistics for backward compatibility
        const legacyProgress = {
          overall_summary: comprehensiveData.overall_summary,
          recent_sessions: comprehensiveData.recent_sessions,
          lessonStats: comprehensiveData.lesson_statistics || {},
        };

        setBackendProgress(legacyProgress);

        // Update completed lessons set from lesson statistics
        const completedIds = Object.keys(
          comprehensiveData.lesson_statistics || {}
        ).filter((id) => comprehensiveData.lesson_statistics[id].is_completed);
        setCompletedLessons(new Set(completedIds));
      }

      // Fallback to localStorage if backend fails
      if (!comprehensiveData) {
        loadLocalStorageProgress();
      }
    } catch (error) {
      console.error('Error loading progress data:', error);
      if (error?.message === 'Authentication failed') {
        redirectToLogin();
        return;
      }
      setError('Failed to load progress data');
      loadLocalStorageProgress();
    } finally {
      setLoading(false);
    }
  };

  const loadLocalStorageProgress = () => {
    try {
      const progress = JSON.parse(
        localStorage.getItem('concepts_mastery_progress') || '[]'
      );
      const completed = new Set(
        progress
          .filter((lesson) => lesson.score >= 70)
          .map((lesson) => lesson.lessonId)
      );
      setCompletedLessons(completed);
    } catch (error) {
      console.error('Error loading localStorage progress:', error);
    }
  };

  // Force refresh by clearing cache and reloading
  const handleCacheRefresh = async () => {
    if (!userEmail) return;

    try {
      // Clear the cache for this user
      clearQuantProgressCache(userEmail);
      console.log('Cache cleared, reloading fresh data...');

      // Reload data which will now fetch from backend
      await loadProgressData();
    } catch (error) {
      console.error('Error refreshing cache:', error);
      setError('Failed to refresh data');
    }
  };

  // Development utility to check cache status (available in console)
  const checkCacheStatus = () => {
    if (!userEmail) {
      console.log('No user email available for cache check');
      return;
    }

    try {
      const cacheKey = `liwaza_quant_progress_${userEmail}`;
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        console.log(`No cache found for ${userEmail}`);
        return;
      }

      const { timestamp } = JSON.parse(cached);
      const now = Date.now();
      const age = now - timestamp;
      const ageMinutes = Math.floor(age / (1000 * 60));
      const isExpired = age > 60 * 60 * 1000; // 1 hour

      console.log(`Cache status for ${userEmail}:`, {
        age: `${ageMinutes} minutes`,
        isExpired,
        cachedAt: new Date(timestamp).toLocaleString(),
        expiresAt: new Date(timestamp + 60 * 60 * 1000).toLocaleString(),
      });
    } catch (error) {
      console.error('Error checking cache status:', error);
    }
  };

  // Make checkCacheStatus available globally for debugging
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.checkQuantCacheStatus = checkCacheStatus;
    }
  }, [userEmail]);

  const getLessonProgress = (conceptId) => {
    if (backendProgress?.lessonStats?.[conceptId]) {
      return backendProgress.lessonStats[conceptId];
    }

    // Fallback to localStorage
    try {
      const progress = JSON.parse(
        localStorage.getItem('concepts_mastery_progress') || '[]'
      );
      const lessonProgress = progress.find(
        (lesson) => lesson.lessonId === conceptId
      );
      if (lessonProgress) {
        return {
          best_score: lessonProgress.score,
          total_attempts: 1,
          last_attempted: lessonProgress.completedAt,
          is_completed: lessonProgress.score >= 70,
          is_mastered: lessonProgress.score >= 90,
        };
      }
    } catch (error) {
      console.error('Error getting lesson progress:', error);
    }

    return null;
  };

  const getStatusInfo = (progress) => {
    if (!progress) {
      return { status: 'not_started', color: '#6b7280', text: 'Not Started' };
    }

    if (progress.is_mastered) {
      return { status: 'mastered', color: '#059669', text: 'Mastered' };
    }

    if (progress.is_completed) {
      return { status: 'completed', color: '#0d9488', text: 'Completed' };
    }

    if (progress.best_score > 0) {
      return { status: 'attempted', color: '#f59e0b', text: 'Needs Practice' };
    }

    return { status: 'not_started', color: '#6b7280', text: 'Not Started' };
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'basic':
        return '#10B981';
      case 'intermediate':
        return '#F59E0B';
      case 'advanced':
        return '#EF4444';
      default:
        return '#6366F1';
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getTopicCategories = () => {
    const topics = new Set();
    conceptsData.forEach((concept) => {
      concept.topics.forEach((topic) => topics.add(topic));
    });
    return Array.from(topics).sort();
  };

  const filteredConcepts = conceptsData.filter((concept) => {
    const difficultyMatch =
      selectedDifficulty === 'ALL' || concept.difficulty === selectedDifficulty;
    const topicMatch =
      selectedTopic === 'ALL' ||
      concept.topics.some((topic) => topic.includes(selectedTopic));
    return difficultyMatch && topicMatch;
  });

  const handleStartLesson = (concept) => {
    navigate(`/exams/gre/concepts/${concept.id}`);
  };

  const renderProgressOverview = () => {
    const totalLessons = conceptsData.length;
    let completed = 0;
    let mastered = 0;
    let attempted = 0;
    let totalAttempts = 0;
    let totalScore = 0;
    let lessonsWithScores = 0;

    // Debug logging
    console.log('=== Progress Overview Debug ===');
    console.log('backendProgress:', backendProgress);
    console.log('comprehensiveProgress:', comprehensiveProgress);
    console.log('completedLessons:', completedLessons);

    // Try comprehensiveProgress first, then backendProgress, then localStorage
    const lessonStats =
      comprehensiveProgress?.lesson_statistics ||
      backendProgress?.lessonStats ||
      {};

    console.log('Lesson statistics to use:', lessonStats);

    // Calculate accurate stats from backend/comprehensive data
    if (lessonStats && Object.keys(lessonStats).length > 0) {
      console.log('Using lesson statistics:', lessonStats);
      Object.values(lessonStats).forEach((stats) => {
        if (stats.is_mastered) mastered++;
        if (stats.is_completed) completed++;
        if (stats.best_score > 0) {
          attempted++;
          totalScore += stats.best_score;
          lessonsWithScores++;
        }
        totalAttempts += stats.total_attempts;
      });
    } else {
      // Fallback to localStorage
      console.log('Using localStorage fallback');
      completed = completedLessons.size;

      // Also calculate from localStorage for other stats
      try {
        const localProgress = JSON.parse(
          localStorage.getItem('concepts_mastery_progress') || '[]'
        );
        console.log('localStorage progress:', localProgress);

        localProgress.forEach((lesson) => {
          if (lesson.score >= 90) mastered++;
          if (lesson.score >= 70) completed++; // >= 70 is completed (includes mastered)
          if (lesson.score > 0) {
            attempted++;
            totalScore += lesson.score;
            lessonsWithScores++;
          }
          totalAttempts++;
        });
      } catch (error) {
        console.error('Error reading localStorage:', error);
      }
    }

    console.log('Final stats:', {
      completed,
      mastered,
      attempted,
      totalScore,
      lessonsWithScores,
      totalAttempts,
    });
    console.log('=== End Debug ===');

    const progressPercentage = Math.round((completed / totalLessons) * 100);
    const averageScore =
      lessonsWithScores > 0 ? Math.round(totalScore / lessonsWithScores) : 0;
    const totalSessions =
      backendProgress?.overall_summary?.total_practice_sessions ||
      totalAttempts;

    return (
      <div
        style={{
          background:
            'linear-gradient(135deg, var(--liwaza-primary-container) 0%, var(--liwaza-secondary-container) 100%)',
          padding: '2rem',
          borderRadius: '16px',
          marginBottom: '2rem',
          color: 'var(--liwaza-on-primary-container)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h2
            style={{
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <FiBookOpen />
            Your Concepts Mastery Progress
          </h2>

          <button
            onClick={handleCacheRefresh}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'var(--liwaza-on-primary-container)',
              border: 'none',
              padding: '0.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
            title="Refresh Progress Data (clears cache)"
          >
            <FiRefreshCw />
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                marginBottom: '0.25rem',
              }}
            >
              {completed}/{totalLessons}
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>
              Lessons Completed
            </div>
            <div
              style={{ opacity: 0.7, fontSize: '0.8rem', marginTop: '0.25rem' }}
            >
              {mastered} mastered
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                marginBottom: '0.25rem',
              }}
            >
              {progressPercentage}%
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>Progress</div>
            <div
              style={{ opacity: 0.7, fontSize: '0.8rem', marginTop: '0.25rem' }}
            >
              {attempted} attempted
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                marginBottom: '0.25rem',
              }}
            >
              {averageScore}%
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>
              Average Score
            </div>
            <div
              style={{ opacity: 0.7, fontSize: '0.8rem', marginTop: '0.25rem' }}
            >
              {totalSessions} sessions
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                marginBottom: '0.25rem',
              }}
            >
              {Math.round(((totalLessons - completed) * 45) / 60)}h
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>
              Est. Remaining
            </div>
            <div
              style={{ opacity: 0.7, fontSize: '0.8rem', marginTop: '0.25rem' }}
            >
              {totalLessons - attempted} not started
            </div>
          </div>
        </div>

        <div
          style={{
            height: '8px',
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPercentage}%`,
              height: '100%',
              background: 'var(--liwaza-primary)',
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
    );
  };

  const renderQuantTrackProgress = () => {
    const completedCount = completedLessons.size;
    const rawWeek = Math.floor(completedCount / 1) + 1;
    const quantWeekNumber = getEffectiveQuantWeekNumber(rawWeek, quantTrack);
    const module = getQuantWeekendModule(quantWeekNumber);
    const progressPercent = Math.round(
      (Math.min(quantWeekNumber, QUANT_WEEKEND_MODULES.length) /
        QUANT_WEEKEND_MODULES.length) *
        100
    );
    const isPendingDiagnostic = quantTrack === QUANT_TRACKS.PENDING_DIAGNOSTIC;
    const isFoundations = quantTrack === QUANT_TRACKS.FOUNDATIONS;
    const recommendedTrack = getRecommendedQuantTrack();
    const ctaPath = isPendingDiagnostic
      ? '/exams/gre/full/full-test-1'
      : `/exams/gre/concepts/${module.lessonId}`;
    const ctaLabel = isPendingDiagnostic ? 'Start Diagnostic' : 'Continue module';
    const pathNote = isPendingDiagnostic
      ? 'Complete the GRE diagnostic to personalize Quant. Meanwhile, start with Arithmetic Foundations, then Math Translation, then Math Shortcuts.'
      : isFoundations
        ? recommendedTrack === QUANT_TRACKS.FOUNDATIONS
          ? 'Recommended from your diagnostic score. Start with Arithmetic Foundations, then Math Translation, then Math Shortcuts before GRE topics. Switch to Acceleration if you want to skip ahead.'
          : 'You chose the Foundations path. Start with Arithmetic Foundations, then Math Translation, then Math Shortcuts before GRE topics.'
        : recommendedTrack === QUANT_TRACKS.FOUNDATIONS
          ? 'You switched to Acceleration to move faster. This starts at GRE topics and skips the first three foundation modules.'
          : null;

    const handleTrackChoice = (track) => {
      setQuantTrack(setQuantTrackChoice(track));
    };

    const trackButtonStyle = (isActive) => ({
      border: 'none',
      borderRadius: '6px',
      padding: '0.4rem 0.75rem',
      background: isActive
        ? 'var(--liwaza-primary)'
        : 'var(--liwaza-surface-container-highest)',
      color: isActive ? 'white' : 'var(--liwaza-on-surface)',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '0.85rem',
    });

    return (
      <div
        style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          borderRadius: '12px',
          background: 'var(--liwaza-surface-container)',
          border: '1px solid var(--liwaza-outline-variant)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '1rem',
            alignItems: 'center',
            marginBottom: pathNote ? '0.5rem' : '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <strong>Quant track: {getQuantTrackLabel(quantTrack)}</strong>
            <p style={{ margin: '0.25rem 0 0', color: 'var(--liwaza-on-surface-variant)' }}>
              Module {quantWeekNumber}/{QUANT_WEEKEND_MODULES.length}: {module.label}
            </p>
            {!isPendingDiagnostic ? (
              <div
                style={{
                  display: 'flex',
                  gap: '0.4rem',
                  marginTop: '0.65rem',
                  flexWrap: 'wrap',
                }}
              >
                <button
                  type="button"
                  onClick={() => handleTrackChoice(QUANT_TRACKS.FOUNDATIONS)}
                  style={trackButtonStyle(isFoundations)}
                >
                  Foundations
                </button>
                <button
                  type="button"
                  onClick={() => handleTrackChoice(QUANT_TRACKS.ACCELERATION)}
                  style={trackButtonStyle(!isFoundations)}
                >
                  Acceleration
                </button>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => navigate(ctaPath)}
            style={{
              border: 'none',
              borderRadius: '8px',
              padding: '0.65rem 0.9rem',
              background: 'var(--liwaza-primary)',
              color: 'white',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {ctaLabel}
            {isPendingDiagnostic ? <FiArrowRight /> : null}
          </button>
        </div>
        {pathNote ? (
          <p
            style={{
              margin: '0 0 0.75rem',
              color: 'var(--liwaza-on-surface-variant)',
              lineHeight: 1.5,
            }}
          >
            {pathNote}
          </p>
        ) : null}
        <div
          style={{
            height: '8px',
            borderRadius: '999px',
            background: 'var(--liwaza-surface-container-highest)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: 'var(--liwaza-primary)',
            }}
          />
        </div>
      </div>
    );
  };

  const renderFilters = () => (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '2rem',
        padding: '1rem',
        background: 'var(--liwaza-surface-container)',
        borderRadius: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontWeight: 600, color: 'var(--liwaza-on-surface)' }}>
          Difficulty:
        </span>
        {['ALL', 'Foundations', 'Basic', 'Intermediate', 'Advanced'].map((difficulty) => (
          <button
            key={difficulty}
            onClick={() => setSelectedDifficulty(difficulty)}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '6px',
              background:
                selectedDifficulty === difficulty
                  ? 'var(--liwaza-primary)'
                  : 'var(--liwaza-surface-container-highest)',
              color:
                selectedDifficulty === difficulty
                  ? 'white'
                  : 'var(--liwaza-on-surface)',
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            {difficulty}
          </button>
        ))}
      </div>
    </div>
  );

  const renderConceptCard = (concept) => {
    const progress = getLessonProgress(concept.id);
    const statusInfo = getStatusInfo(progress);

    return (
      <div
        key={concept.id}
        style={{
          background: 'var(--liwaza-surface-container)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: `2px solid ${statusInfo.color}40`,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          transition: 'transform 0.2s, box-shadow 0.2s',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem',
            }}
          >
            <div
              style={{
                background:
                  statusInfo.status === 'mastered'
                    ? 'var(--liwaza-success-container)'
                    : statusInfo.status === 'completed'
                      ? 'var(--liwaza-tertiary-container)'
                      : statusInfo.status === 'attempted'
                        ? 'var(--liwaza-warning-container)'
                        : 'var(--liwaza-primary-container)',
                color:
                  statusInfo.status === 'mastered'
                    ? 'var(--liwaza-success)'
                    : statusInfo.status === 'completed'
                      ? 'var(--liwaza-tertiary)'
                      : statusInfo.status === 'attempted'
                        ? 'var(--liwaza-warning)'
                        : 'var(--liwaza-primary)',
                padding: '0.75rem',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {statusInfo.status === 'mastered' ? (
                <FiTarget size={24} />
              ) : statusInfo.status === 'completed' ? (
                <FiCheck size={24} />
              ) : statusInfo.status === 'attempted' ? (
                <FiAlertCircle size={24} />
              ) : (
                concept.icon
              )}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '0.25rem',
              }}
            >
              <span
                style={{
                  background: getDifficultyColor(concept.difficulty),
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                }}
              >
                {concept.difficulty}
              </span>

              <span
                style={{
                  background: `${statusInfo.color}20`,
                  color: statusInfo.color,
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}
              >
                {statusInfo.status === 'mastered' && <FiTarget size={12} />}
                {statusInfo.status === 'completed' && <FiCheck size={12} />}
                {statusInfo.status === 'attempted' && (
                  <FiAlertCircle size={12} />
                )}
                {statusInfo.text}
              </span>
            </div>
          </div>

          <h3
            style={{
              color: 'var(--liwaza-on-surface)',
              marginBottom: '0.75rem',
              fontSize: '1.2rem',
            }}
          >
            {concept.title}
          </h3>

          <p
            style={{
              color: 'var(--liwaza-on-surface-variant)',
              fontSize: '0.9rem',
              lineHeight: 1.5,
              marginBottom: '1rem',
            }}
          >
            {concept.description}
          </p>

          {/* Progress and Score Information */}
          {progress && progress.best_score > 0 && (
            <div
              style={{
                background: 'var(--liwaza-surface-container-highest)',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                border: `1px solid ${statusInfo.color}30`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                }}
              >
                <span
                  style={{
                    fontSize: '0.9rem',
                    color: 'var(--liwaza-on-surface-variant)',
                  }}
                >
                  Best Score:
                </span>
                <span
                  style={{
                    fontWeight: 'bold',
                    color: statusInfo.color,
                    fontSize: '1rem',
                  }}
                >
                  {progress.best_score}%
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.8rem',
                  color: 'var(--liwaza-on-surface-variant)',
                }}
              >
                <span>
                  {progress.total_attempts} attempt
                  {progress.total_attempts !== 1 ? 's' : ''}
                </span>
                {progress.last_attempted && (
                  <span>
                    Last:{' '}
                    {new Date(progress.last_attempted).toLocaleDateString()}
                  </span>
                )}
              </div>

              {/* Encouragement Message */}
              {statusInfo.status === 'attempted' && (
                <div
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    background: 'var(--liwaza-warning-container)',
                    color: 'var(--liwaza-on-warning-container)',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                  }}
                >
                  <FiTarget size={14} /> Practice more to reach 70% and complete
                  this lesson!
                </div>
              )}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              color: 'var(--liwaza-on-surface-variant)',
              fontSize: '0.9rem',
              marginBottom: '1rem',
            }}
          >
            <span
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <FiClock size={16} />
              {concept.estimatedTime}
            </span>
            <span
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <FiBookOpen size={16} />
              {concept.topics.length} topics
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              marginBottom: '1.5rem',
            }}
          >
            {concept.topics.slice(0, 3).map((topic, index) => (
              <span
                key={index}
                style={{
                  background: 'var(--liwaza-secondary-container)',
                  color: 'var(--liwaza-on-secondary-container)',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                }}
              >
                {topic}
              </span>
            ))}
            {concept.topics.length > 3 && (
              <span
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  fontSize: '0.8rem',
                  padding: '0.25rem 0.5rem',
                }}
              >
                +{concept.topics.length - 3} more
              </span>
            )}
          </div>
        </div>

        <button
          onClick={() => handleStartLesson(concept)}
          style={{
            background:
              statusInfo.status === 'attempted'
                ? '#f59e0b'
                : statusInfo.status === 'completed'
                  ? 'var(--liwaza-secondary)'
                  : statusInfo.status === 'mastered'
                    ? 'var(--liwaza-success)'
                    : 'var(--liwaza-primary)',
            color: 'white',
            border: 'none',
            padding: '0.75rem',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            fontSize: '1rem',
            fontWeight: 500,
          }}
        >
          {statusInfo.status === 'mastered' ? (
            <>
              <FiTarget /> Review Mastered
            </>
          ) : statusInfo.status === 'completed' ? (
            <>
              <FiCheck /> Review Lesson
            </>
          ) : statusInfo.status === 'attempted' ? (
            <>
              <FiPlay /> Try Again
            </>
          ) : (
            <>
              <FiPlay /> Start Lesson
            </>
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="gre-concepts-mastery">
      {loading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            color: 'var(--liwaza-on-surface-variant)',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid var(--liwaza-outline-variant)',
                borderTop: '4px solid var(--liwaza-primary)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem',
              }}
            />
            <p>Loading progress data...</p>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            background: 'var(--liwaza-error-container)',
            color: 'var(--liwaza-on-error-container)',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            textAlign: 'center',
          }}
        >
          <p style={{ margin: '0 0 1rem' }}>
            {getErrorMessage(error, 'Failed to load progress data.')}
          </p>
          <button
            onClick={handleCacheRefresh}
            style={{
              background: 'var(--liwaza-error)',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      )}

      <div style={{ marginBottom: '2rem' }}>
        <div className="gre-concepts-mastery-header">
          <h1
            style={{
              fontSize: '2.5rem',
              color: 'var(--liwaza-on-surface)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <FiBookOpen />
            Concepts Mastery
          </h1>
          <button
            onClick={() => navigate('/exams/gre/concepts/saved')}
            style={{
              background: 'var(--liwaza-primary-container)',
              color: 'var(--liwaza-on-primary-container)',
              border: '2px solid var(--liwaza-primary)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem',
              fontWeight: 500,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--liwaza-primary)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                'var(--liwaza-primary-container)';
              e.currentTarget.style.color =
                'var(--liwaza-on-primary-container)';
            }}
          >
            <FiBook size={20} />
            My Concept Book
          </button>
        </div>
        <p
          style={{
            color: 'var(--liwaza-on-surface-variant)',
            fontSize: '1.1rem',
            marginBottom: '2rem',
          }}
        >
          Master fundamental quantitative concepts with comprehensive lessons,
          examples, and practice exercises.
        </p>
        {renderQuantTrackProgress()}
      </div>

      {!loading && renderProgressOverview()}

      {/* Weekly Progression Analysis */}
      {!loading && comprehensiveProgress && (
        <div style={{ marginBottom: '2rem' }}>
          <h2
            style={{
              fontSize: '1.8rem',
              color: 'var(--liwaza-on-surface)',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <FiTrendingUp />
            Weekly Learning Progression
          </h2>

          {/* Weekly Performance Trends */}
          {comprehensiveProgress?.performance_trends?.weekly_performance &&
            comprehensiveProgress.performance_trends.weekly_performance.length >
              0 && (
              <div
                style={{
                  background: 'var(--liwaza-surface-container)',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  marginBottom: '1.5rem',
                  border: '1px solid var(--liwaza-outline-variant)',
                }}
              >
                <h3
                  style={{
                    marginBottom: '1rem',
                    color: 'var(--liwaza-on-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FiBarChart2 /> Weekly Performance Summary
                </h3>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
                    gap: '1rem',
                  }}
                >
                  {comprehensiveProgress.performance_trends.weekly_performance.map(
                    (week, index) => {
                      const prevWeek =
                        index > 0
                          ? comprehensiveProgress.performance_trends
                              .weekly_performance[index - 1]
                          : null;
                      const scoreChange = prevWeek
                        ? week.average_score - prevWeek.average_score
                        : 0;
                      const sessionChange = prevWeek
                        ? week.session_count - prevWeek.session_count
                        : 0;

                      const weekDate = new Date(week.week);
                      const weekLabel = `Week of ${weekDate.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;

                      return (
                        <div
                          key={week.week}
                          style={{
                            background:
                              'var(--liwaza-surface-container-highest)',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '1px solid var(--liwaza-outline-variant)',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              color: 'var(--liwaza-on-surface)',
                              marginBottom: '0.5rem',
                            }}
                          >
                            {weekLabel}
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '0.5rem',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                color: 'var(--liwaza-primary)',
                              }}
                            >
                              {Math.round(week.average_score)}%
                            </span>
                            {scoreChange !== 0 && (
                              <span
                                style={{
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  color:
                                    scoreChange > 0
                                      ? 'var(--liwaza-success)'
                                      : 'var(--liwaza-error)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                }}
                              >
                                {scoreChange > 0 ? '↗' : '↘'}{' '}
                                {Math.abs(scoreChange).toFixed(1)}%
                              </span>
                            )}
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              fontSize: '0.8rem',
                              color: 'var(--liwaza-on-surface-variant)',
                            }}
                          >
                            <span>{week.session_count} sessions</span>
                            {sessionChange !== 0 && (
                              <span
                                style={{
                                  color:
                                    sessionChange > 0
                                      ? 'var(--liwaza-success)'
                                      : 'var(--liwaza-warning)',
                                }}
                              >
                                {sessionChange > 0 ? '+' : ''}
                                {sessionChange}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}

          {/* Daily Progression (Last 2 Weeks) */}
          {comprehensiveProgress?.timeline_progression?.daily_progression &&
            comprehensiveProgress.timeline_progression.daily_progression
              .length > 0 && (
              <div
                style={{
                  background: 'var(--liwaza-surface-container)',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  marginBottom: '1.5rem',
                  border: '1px solid var(--liwaza-outline-variant)',
                }}
              >
                <h3
                  style={{
                    marginBottom: '1rem',
                    color: 'var(--liwaza-on-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FiActivity /> Daily Concept Progress (Last{' '}
                  {
                    comprehensiveProgress.timeline_progression.daily_progression
                      .length
                  }{' '}
                  Days)
                </h3>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                  }}
                >
                  {comprehensiveProgress.timeline_progression.daily_progression
                    .slice(
                      -comprehensiveProgress.timeline_progression
                        .daily_progression.length
                    )
                    .map((day, index) => {
                      const dayDate = new Date(day.date);
                      const isToday =
                        dayDate.toDateString() === new Date().toDateString();
                      const hasActivity = day.sessions_completed > 0;

                      return (
                        <div
                          key={day.date}
                          style={{
                            background: hasActivity
                              ? isToday
                                ? 'var(--liwaza-primary-container)'
                                : 'var(--liwaza-surface-container-highest)'
                              : 'var(--liwaza-outline-variant)',
                            padding: '0.75rem 0.5rem',
                            borderRadius: '6px',
                            textAlign: 'center',
                            border: isToday
                              ? '2px solid var(--liwaza-primary)'
                              : '1px solid transparent',
                            opacity: hasActivity ? 1 : 0.6,
                          }}
                        >
                          <div
                            style={{
                              fontSize: '0.7rem',
                              color: 'var(--liwaza-on-surface-variant)',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {dayDate.toLocaleDateString([], {
                              weekday: 'short',
                              month: 'numeric',
                              day: 'numeric',
                            })}
                          </div>

                          {hasActivity ? (
                            <>
                              <div
                                style={{
                                  fontSize: '1rem',
                                  fontWeight: 'bold',
                                  color: 'var(--liwaza-primary)',
                                  marginBottom: '0.25rem',
                                }}
                              >
                                {Math.round(day.avg_score)}%
                              </div>
                              <div
                                style={{
                                  fontSize: '0.7rem',
                                  color: 'var(--liwaza-on-surface-variant)',
                                }}
                              >
                                {day.sessions_completed} session
                                {day.sessions_completed !== 1 ? 's' : ''}
                              </div>
                              <div
                                style={{
                                  fontSize: '0.7rem',
                                  color: 'var(--liwaza-on-surface-variant)',
                                }}
                              >
                                {day.total_time_minutes}min
                              </div>
                              <div
                                style={{
                                  fontSize: '0.7rem',
                                  color: 'var(--liwaza-secondary)',
                                  fontWeight: '600',
                                }}
                              >
                                {day.total_questions}Q
                              </div>
                            </>
                          ) : (
                            <div
                              style={{
                                fontSize: '0.8rem',
                                color: 'var(--liwaza-on-surface-variant)',
                                opacity: 0.7,
                              }}
                            >
                              No practice
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>

                {/* Two-Week Summary Stats */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid var(--liwaza-outline-variant)',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        color: 'var(--liwaza-primary)',
                      }}
                    >
                      {
                        comprehensiveProgress.timeline_progression.daily_progression.filter(
                          (d) => d.sessions_completed > 0
                        ).length
                      }
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--liwaza-on-surface-variant)',
                      }}
                    >
                      Active Days
                    </div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        color: 'var(--liwaza-secondary)',
                      }}
                    >
                      {Math.round(
                        comprehensiveProgress.timeline_progression.daily_progression
                          .filter((d) => d.sessions_completed > 0)
                          .reduce((sum, d) => sum + d.avg_score, 0) /
                          (comprehensiveProgress.timeline_progression.daily_progression.filter(
                            (d) => d.sessions_completed > 0
                          ).length || 1)
                      )}
                      %
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--liwaza-on-surface-variant)',
                      }}
                    >
                      Avg Score
                    </div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        color: 'var(--liwaza-tertiary)',
                      }}
                    >
                      {comprehensiveProgress.timeline_progression.daily_progression.reduce(
                        (sum, d) => sum + d.total_questions,
                        0
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--liwaza-on-surface-variant)',
                      }}
                    >
                      Total Questions
                    </div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        color: 'var(--liwaza-success)',
                      }}
                    >
                      {Math.round(
                        (comprehensiveProgress.timeline_progression.daily_progression.reduce(
                          (sum, d) => sum + d.total_correct,
                          0
                        ) /
                          (comprehensiveProgress.timeline_progression.daily_progression.reduce(
                            (sum, d) => sum + d.total_questions,
                            0
                          ) || 1)) *
                          100
                      )}
                      %
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--liwaza-on-surface-variant)',
                      }}
                    >
                      Overall Accuracy
                    </div>
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        color: 'var(--liwaza-warning)',
                      }}
                    >
                      {Math.round(
                        comprehensiveProgress.timeline_progression.daily_progression.reduce(
                          (sum, d) => sum + d.total_time_minutes,
                          0
                        ) / 60
                      )}
                      h
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--liwaza-on-surface-variant)',
                      }}
                    >
                      Total Study Time
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Recent Lesson Progress */}
          {comprehensiveProgress?.recent_sessions &&
            comprehensiveProgress.recent_sessions.length > 0 && (
              <div
                style={{
                  background: 'var(--liwaza-surface-container)',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: '1px solid var(--liwaza-outline-variant)',
                }}
              >
                <h3
                  style={{
                    marginBottom: '1rem',
                    color: 'var(--liwaza-on-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FiCalendar /> Recent Lesson Activity
                </h3>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '1rem',
                  }}
                >
                  {comprehensiveProgress.recent_sessions
                    .slice(0, 6)
                    .map((session, index) => {
                      const scoreColor =
                        session.score_percentage >= 90
                          ? 'var(--liwaza-success)'
                          : session.score_percentage >= 70
                            ? 'var(--liwaza-warning)'
                            : 'var(--liwaza-error)';

                      const sessionDate = new Date(
                        session.completed_at
                      ).toLocaleDateString();
                      const sessionTime = new Date(
                        session.completed_at
                      ).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      });

                      return (
                        <div
                          key={index}
                          style={{
                            background:
                              'var(--liwaza-surface-container-highest)',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: `2px solid ${scoreColor}20`,
                            borderLeft: `4px solid ${scoreColor}`,
                          }}
                        >
                          <div
                            style={{
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              color: 'var(--liwaza-on-surface)',
                              marginBottom: '0.5rem',
                            }}
                          >
                            {getLessonNameById(session.lesson_id)}
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '0.5rem',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                                color: scoreColor,
                              }}
                            >
                              {Math.round(session.score_percentage)}%
                            </span>
                            <span
                              style={{
                                fontSize: '0.8rem',
                                color: 'var(--liwaza-on-surface-variant)',
                                textTransform: 'capitalize',
                              }}
                            >
                              {session.practice_mode || 'Practice'}
                            </span>
                          </div>

                          <div
                            style={{
                              fontSize: '0.7rem',
                              color: 'var(--liwaza-on-surface-variant)',
                            }}
                          >
                            {sessionDate} • {sessionTime}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
        </div>
      )}

      {!loading && renderFilters()}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem',
        }}
      >
        {!loading && filteredConcepts.map(renderConceptCard)}
      </div>

      <div
        style={{
          textAlign: 'center',
          marginTop: '3rem',
          padding: '2rem',
          background: 'var(--liwaza-surface-container-lowest)',
          borderRadius: '12px',
        }}
      >
        <h3
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1rem',
          }}
        >
          Progress Tracking
        </h3>
        <p
          style={{
            color: 'var(--liwaza-on-surface-variant)',
            marginBottom: '1.5rem',
          }}
        >
          • <strong>70% or higher:</strong> Lesson completed{' '}
          <FiCheck
            style={{ color: 'var(--liwaza-success)', marginLeft: '0.25rem' }}
          />
          <br />• <strong>90% or higher:</strong> Lesson mastered{' '}
          <FiTarget
            style={{ color: 'var(--liwaza-success)', marginLeft: '0.25rem' }}
          />
          <br />• <strong>Below 70%:</strong> Keep practicing to complete the
          lesson
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'var(--liwaza-primary-container)',
              borderRadius: '8px',
            }}
          >
            <FiBookOpen />
            <span>Detailed Examples</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'var(--liwaza-secondary-container)',
              borderRadius: '8px',
            }}
          >
            <FiTarget />
            <span>Practice Exercises</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'var(--liwaza-success-container)',
              borderRadius: '8px',
            }}
          >
            <FiClock />
            <span>Timed Practice</span>
          </div>
        </div>
      </div>
    </div>
  );
}
